import { useMemo } from 'react';
import type React from 'react';

import merge from 'lodash.merge';

import { cn } from '@/shadcn/lib/utils';

import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import { useWidgetRenderContext } from '@/components/ui/widget/widgetRenderContext';
import { getByPath } from '@/utils/widgets';
import type { WidgetData } from '../../widget.type';
import type { IconView } from './icon.view.type';

type Props = {
  view: IconView;
  data: WidgetData;
  update: {
    patch: WidgetData;
  };
};

const IconViewImpl: React.FC<Props> = ({ view, data, update }) => {
  const { path, iconPath, labelPath, icon } = view;
  const { patch } = update || {};
  const renderContext = useWidgetRenderContext();
  // check if the data to display has been patched
  const dataWithPatchApplied = useMemo(
    () => merge({}, data, patch),
    [data, patch]
  );

  // if a 'path' has been specified we assume this is to check whether the icon should be displayed or not
  if (path && !getByPath(dataWithPatchApplied, path)) {
    return null;
  }
  const iconStr =
    getByPath(dataWithPatchApplied, iconPath) || icon || 'Document';
  const iconComponent = <DynamicIcon icon={iconStr} size={24} />;
  const label = getByPath(dataWithPatchApplied, labelPath);

  return (
    <div
      className={cn(
        'flex h-full w-full flex-row items-center justify-start overflow-hidden',
        renderContext === 'table-cell' && !label && 'justify-center'
      )}
    >
      <span className="flex-none">{iconComponent}</span>
      {label && <span className="ml-[5px] truncate">{label}</span>}
    </div>
  );
};

// sort defaults to text on iconPath or labelPath, or binary on path
const getSortConfig = (view: IconView) => {
  const { path, labelPath, iconPath } = view;
  if (!(labelPath || iconPath)) return { type: 'binary', path };
  return {
    type: 'text',
    path: labelPath || iconPath
  };
};

// filtering defaults to text search on iconPath
const getFilterConfig = (view: IconView) => ({
  type: 'text',
  path: view.labelPath || view.iconPath || view.path
});

// width defaults to 'medium' if a label is shown, 'small' otherwise
const getDefaultWidth = (view: IconView): ColumnWidth =>
  view.labelPath ? 'medium' : 'small';

// stringifies data at path
const getStringifyFunction = (view: IconView) => {
  const { path, labelPath } = view;

  // output either labelPath if specified
  if (labelPath !== undefined)
    return (data: WidgetData) => getByPath(data, labelPath);

  // or just the boolean value associated to path (that triggered the icon rendering)
  if (path !== undefined)
    return (data: WidgetData) => (!!getByPath(data, path)).toString();

  return () => '';
};

export const iconViewProvider = {
  component: IconViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
